import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { entrarComoAdmin, mensagemDoErro, sairDoAdmin } from "@/lib/login-do-admin";

const campo =
  "w-full rounded-lg border border-admin-800 bg-admin-900 px-3 py-2 text-sm text-white placeholder-admin-200/30 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const rotulo = "text-xs font-medium uppercase tracking-wider text-admin-200/70";

export function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [pedeCodigo, setPedeCodigo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const situacao = trpc.mfaDoAdmin.situacao.useQuery(undefined, { retry: false });

  // Já entrou com o segundo fator? Vai direto.
  useEffect(() => {
    if (situacao.data?.naSessao) navigate(redirect, { replace: true });
  }, [situacao.data, navigate, redirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const erro = await entrarComoAdmin(email, password, pedeCodigo ? codigo : undefined);
      if (erro?.includes("MFA_NECESSARIO")) {
        setPedeCodigo(true);
        return;
      }
      if (erro) {
        setError(mensagemDoErro(erro));
        return;
      }

      const me = await utils.me.current.fetch();
      if (me?.platformRole !== "ADMIN") {
        setError("Esta conta não tem permissão de administrador.");
        await sairDoAdmin();
        return;
      }
      await utils.mfaDoAdmin.situacao.invalidate();
      toast.success("Bem-vindo, admin.");
      navigate(redirect, { replace: true });
    } catch {
      setError("Falha no login. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-admin-900 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500 text-white shadow-lg">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-white">DentalBook Admin</h1>
          <p className="mt-1 text-sm text-admin-200/70">Área restrita a administradores da plataforma.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-admin-800 bg-admin-950 p-6 shadow-xl">
          {pedeCodigo ? (
            <div className="space-y-1.5">
              <label className={rotulo}>Código de verificação</label>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="123456"
                required
                autoFocus
                className={campo}
              />
              <p className="text-xs text-admin-200/60">
                O código de 6 dígitos do aplicativo autenticador, ou um dos códigos de recuperação.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className={rotulo}>E-mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@dentalbookapp.com.br" required autoFocus className={campo} />
              </div>
              <div className="space-y-1.5">
                <label className={rotulo}>Senha</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className={campo} />
              </div>
            </>
          )}

          {error && <p className="rounded-lg border border-red-800 bg-red-900/30 px-3 py-2 text-sm text-red-300">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-red-500 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-colors hover:bg-red-600 disabled:opacity-50"
          >
            {loading ? "Entrando..." : pedeCodigo ? "Confirmar código" : "Entrar como admin"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-admin-200/50">
          Usuários regulares acessam em{" "}
          <a href={import.meta.env.VITE_LANDING_URL || "http://localhost:3003"} className="underline hover:text-admin-200">
            dentalbookapp.com.br
          </a>
        </p>
      </div>
    </div>
  );
}
